import { apiRequest, ApiRequestError } from '../api';
import { saveSession } from '../state';
import { t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import type { LoginResponse, SignupResponse } from '../types';

interface AuthCallbacks {
    onLoginSuccess: () => void;
    renderUserBox: () => void;
}

// Типы Turnstile (Cloudflare) — объявляем глобально, т.к. либы нет.
declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, opts: Record<string, unknown>) => string;
            getResponse: (widgetId?: string) => string | undefined;
            reset: (widgetId?: string) => void;
        };
    }
}

interface CaptchaConfig {
    enabled: boolean;
    siteKey: string | null;
}

let captchaEnabled = false;
let captchaWidget: string | undefined;
let captchaInitPromise: Promise<void> | null = null;

/** Загружает скрипт Turnstile один раз и инициализирует виджет. */
function initCaptcha(container: HTMLElement): void {
    if (captchaInitPromise) return;
    captchaInitPromise = (async () => {
        try {
            const cfg = await apiRequest<CaptchaConfig>('/api/auth/captcha-config');
            captchaEnabled = cfg.enabled && Boolean(cfg.siteKey);
            if (!captchaEnabled) return;

            if (!window.turnstile) await loadTurnstileScript();
            if (window.turnstile && cfg.siteKey) {
                captchaWidget = window.turnstile.render(container, {
                    sitekey: cfg.siteKey,
                    theme: 'dark',
                });
            }
        } catch {
            captchaEnabled = false;
        }
    })();
}

function loadTurnstileScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(
            'script[src*="challenges.cloudflare.com/turnstile"]'
        );
        if (existing) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('failed to load captcha'));
        document.head.appendChild(script);
    });
}

function getCaptchaToken(): string | undefined {
    if (!captchaEnabled) return undefined;
    return window.turnstile?.getResponse(captchaWidget);
}

/**
 * Блокирует/разблокирует форму на время отправки и подменяет текст кнопки,
 * чтобы не казалось, будто страница «зависла».
 */
function setFormBusy(form: HTMLFormElement, busy: boolean, label?: string): void {
    form.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        'input, button, textarea, select'
    ).forEach((el) => {
        el.disabled = busy;
    });
    form.classList.toggle('is-busy', busy);

    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!btn) return;
    if (busy) {
        if (btn.dataset.idleLabel === undefined) {
            btn.dataset.idleLabel = btn.textContent ?? '';
        }
        if (label) btn.textContent = label;
    } else if (btn.dataset.idleLabel !== undefined) {
        btn.textContent = btn.dataset.idleLabel;
    }
}

function resetCaptcha(): void {
    if (captchaEnabled && window.turnstile) window.turnstile.reset(captchaWidget);
}

// Ошибки авторизации с сервера по коду → локализованный текст.
const AUTH_ERROR_KEYS: Record<string, TranslationKey> = {
    invalid_credentials: 'auth.error_invalid_credentials',
    email_not_confirmed: 'auth.error_email_not_confirmed',
    account_banned: 'auth.error_account_banned',
    username_taken: 'auth.error_username_taken',
};

/** Локализует ошибку по коду сервера; иначе — префикс + исходное сообщение. */
function localizedAuthError(err: unknown, fallbackKey: TranslationKey): string {
    const key =
        err instanceof ApiRequestError && err.code
            ? AUTH_ERROR_KEYS[err.code]
            : undefined;
    if (key) return t(key);
    return t(fallbackKey) + (err instanceof Error ? err.message : String(err));
}

/**
 * Догружает роли, username, привязанного игрока через /api/auth/me
 * и сохраняет всё в session. Возвращает обновлённого user или null.
 */
export async function fetchAndSaveFullUser(
    accessToken: string,
    refreshToken?: string
): Promise<{ id: string; email: string } | null> {
    try {
        const me = await apiRequest<{
            user: {
                id: string;
                email: string;
                username: string | null;
                is_moderator: boolean;
                is_admin: boolean;
                is_ghost: boolean;
                player_id: number | null;
                player_name: string | null;
            };
        }>('/api/auth/me', { token: accessToken });

        saveSession(
            {
                id: me.user.id,
                email: me.user.email,
                username: me.user.username,
                is_moderator: me.user.is_moderator,
                is_admin: me.user.is_admin,
                is_ghost: me.user.is_ghost,
                player_id: me.user.player_id,
                player_name: me.user.player_name,
            },
            accessToken,
            refreshToken
        );

        return { id: me.user.id, email: me.user.email };
    } catch (err) {
        console.warn('[auth] failed to fetch /me after login:', err);
        return null;
    }
}

export function bindAuth(cb: AuthCallbacks): () => void {
    const formLogin = document.getElementById('form-login') as HTMLFormElement | null;
    const formSignup = document.getElementById('form-signup') as HTMLFormElement | null;
    const formForgot = document.getElementById('form-forgot') as HTMLFormElement | null;
    const cardLogin = document.getElementById('card-login');
    const cardSignup = document.getElementById('card-signup');
    const cardForgot = document.getElementById('card-forgot');
    const screenAuth = document.getElementById('screen-auth');
    if (!formLogin || !formSignup || !formForgot || !cardLogin || !cardSignup || !cardForgot || !screenAuth)
        return () => {};

    const showCard = (card: HTMLElement) => {
        cardLogin.classList.add('hidden');
        cardSignup.classList.add('hidden');
        cardForgot.classList.add('hidden');
        card.classList.remove('hidden');
    };

    const cleanup: Array<() => void> = [];

    // Сбрасываем возможное «залипание» формы (disabled) после прошлой отправки.
    setFormBusy(formLogin, false);
    setFormBusy(formSignup, false);
    setFormBusy(formForgot, false);

    // Кнопка «показать пароль» в поле регистрации.
    const onTogglePassword = (e: Event) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.password-toggle');
        if (!btn) return;
        e.preventDefault();
        const input = btn.closest('.password-field')?.querySelector<HTMLInputElement>('input');
        if (!input) return;
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        btn.classList.toggle('is-visible', show);
        btn.title = show ? t('auth.hide_password') : t('auth.show_password');
    };
    screenAuth.addEventListener('click', onTogglePassword);
    cleanup.push(() => screenAuth.removeEventListener('click', onTogglePassword));

    // Ссылки переключения форм
    const onSwitchToSignup = (e: Event) => {
        e.preventDefault();
        showCard(cardSignup);

        // Инициализируем CAPTCHA, когда карточка регистрации видна.
        const captchaContainer = document.getElementById('captcha-container');
        if (captchaContainer) initCaptcha(captchaContainer);
    };
    document.getElementById('link-to-signup')?.addEventListener('click', onSwitchToSignup);
    cleanup.push(() => document.getElementById('link-to-signup')?.removeEventListener('click', onSwitchToSignup));

    const onSwitchToLogin = (e: Event) => {
        e.preventDefault();
        showCard(cardLogin);
    };
    document.getElementById('link-to-login')?.addEventListener('click', onSwitchToLogin);
    cleanup.push(() => document.getElementById('link-to-login')?.removeEventListener('click', onSwitchToLogin));

    const onSwitchToForgot = (e: Event) => {
        e.preventDefault();
        showCard(cardForgot);
    };
    document.getElementById('link-to-forgot')?.addEventListener('click', onSwitchToForgot);
    cleanup.push(() => document.getElementById('link-to-forgot')?.removeEventListener('click', onSwitchToForgot));

    const onForgotToLogin = (e: Event) => {
        e.preventDefault();
        showCard(cardLogin);
    };
    document.getElementById('link-forgot-to-login')?.addEventListener('click', onForgotToLogin);
    cleanup.push(() => document.getElementById('link-forgot-to-login')?.removeEventListener('click', onForgotToLogin));

    // ============================================================
    // Логин
    // ============================================================
    let loginSubmitting = false;
    const onLoginSubmit = async (e: Event) => {
        e.preventDefault();
        if (loginSubmitting) return;

        const fd = new FormData(formLogin);
        const identifier = String(fd.get('identifier') ?? '').trim();
        const password = String(fd.get('password') ?? '');

        if (!identifier) {
            alert(t('auth.error_identifier_required'));
            return;
        }
        if (!password) {
            alert(t('auth.error_password_required'));
            return;
        }

        loginSubmitting = true;
        setFormBusy(formLogin, true, t('auth.login_loading'));
        try {
            const res = await apiRequest<LoginResponse>('/api/auth/login', {
                method: 'POST',
                body: { identifier, password },
            });

            // Сохраняем токены и сразу переходим, не дожидаясь догрузки ролей:
            // иначе форма висит, пока не ответит /api/auth/me.
            saveSession(res.user, res.access_token, res.refresh_token, res.expires_at * 1000);
            cb.renderUserBox();
            cb.onLoginSuccess();

            // Роли/username/player_id догружаем в фоне (обновит шапку).
            void fetchAndSaveFullUser(res.access_token, res.refresh_token);
        } catch (err) {
            alert(localizedAuthError(err, 'auth.error_login'));
        } finally {
            loginSubmitting = false;
            // Если остались на /auth (ошибка) — вернуть форму.
            setFormBusy(formLogin, false);
        }
    };
    formLogin.addEventListener('submit', onLoginSubmit);
    cleanup.push(() => formLogin.removeEventListener('submit', onLoginSubmit));

    // ============================================================
    // Регистрация
    // ============================================================
    let signupSubmitting = false;
    const onSignupSubmit = async (e: Event) => {
        e.preventDefault();
        if (signupSubmitting) return;

        // Читаем и валидируем ДО setFormBusy: disabled-поля не попадают в FormData.
        const fd = new FormData(formSignup);
        const username = String(fd.get('username') ?? '').trim();
        const email = String(fd.get('email') ?? '').trim();
        const password = String(fd.get('password') ?? '');

        // Клиентская валидация
        if (username.length < 3) {
            alert(t('auth.error_username_short'));
            return;
        }
        if (!email.includes('@')) {
            alert(t('auth.error_email_invalid'));
            return;
        }
        if (password.length < 6) {
            alert(t('auth.error_password_short'));
            return;
        }

        // Дожидаемся инициализации CAPTCHA, чтобы captchaEnabled был финальным.
        if (captchaInitPromise) await captchaInitPromise;

        // CAPTCHA: если включена — токен обязателен. Берём токен до setFormBusy.
        let captchaToken: string | undefined;
        if (captchaEnabled) {
            captchaToken = getCaptchaToken();
            if (!captchaToken) {
                resetCaptcha();
                alert(t('auth.captcha_required'));
                return;
            }
        }

        signupSubmitting = true;
        setFormBusy(formSignup, true, t('auth.signup_loading'));
        try {
            const res = await apiRequest<SignupResponse>('/api/auth/signup', {
                method: 'POST',
                body: { username, email, password, captchaToken },
            });

            if (res.session && res.user) {
                // Confirm email выключен — сразу логиним (не ждём догрузку ролей).
                saveSession(
                    res.user,
                    res.session.access_token,
                    res.session.refresh_token,
                    res.session.expires_at * 1000
                );
                cb.renderUserBox();
                cb.onLoginSuccess();

                void fetchAndSaveFullUser(res.session.access_token, res.session.refresh_token);
            } else {
                // Confirm email включён
                alert(t('auth.account_created_confirm'));
                showCard(cardLogin);
            }
        } catch (err) {
            resetCaptcha();
            alert(localizedAuthError(err, 'auth.error_signup'));
        } finally {
            signupSubmitting = false;
            // Если остались на /auth (ошибка/подтверждение почты) — вернуть форму.
            setFormBusy(formSignup, false);
        }
    };
    formSignup.addEventListener('submit', onSignupSubmit);
    cleanup.push(() => formSignup.removeEventListener('submit', onSignupSubmit));

    // ============================================================
    // Забыли пароль
    // ============================================================
    let forgotSubmitting = false;
    const onForgotSubmit = async (e: Event) => {
        e.preventDefault();
        if (forgotSubmitting) return;
        forgotSubmitting = true;
        try {
            const fd = new FormData(formForgot);
            const identifier = String(fd.get('identifier') ?? '').trim();
            if (!identifier) {
                alert(t('auth.error_identifier_required'));
                return;
            }

            await apiRequest('/api/auth/forgot-password', {
                method: 'POST',
                body: { identifier },
            });

            alert(t('auth.forgot_sent'));
            showCard(cardLogin);
        } catch (err) {
            alert(t('auth.error_login') + (err instanceof Error ? err.message : String(err)));
        } finally {
            forgotSubmitting = false;
        }
    };
    formForgot.addEventListener('submit', onForgotSubmit);
    cleanup.push(() => formForgot.removeEventListener('submit', onForgotSubmit));

    return () => {
        cleanup.forEach((fn) => fn());
        resetCaptcha();
    };
}

/** Токены/ошибка из hash-фрагмента после редиректа Supabase (implicit flow). */
export interface AuthHashTokens {
    accessToken: string | null;
    refreshToken: string | null;
    type: string | null;
    error: string | null;
    errorCode: string | null;
    errorDescription: string | null;
}

export function parseAuthHash(): AuthHashTokens {
    const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
    const params = new URLSearchParams(hash);
    return {
        accessToken: params.get('access_token'),
        refreshToken: params.get('refresh_token'),
        type: params.get('type'),
        error: params.get('error'),
        errorCode: params.get('error_code'),
        errorDescription: params.get('error_description'),
    };
}

/** Токен из recovery-ссылки сброса пароля. */
export function parseRecoveryTokens(): string | null {
    const { accessToken, type } = parseAuthHash();
    if (type !== 'recovery') return null;
    return accessToken;
}

/**
 * Привязывает форму установки нового пароля на странице /reset-password.
 * Возвращает cleanup-функцию.
 */
export function bindReset(
    formReset: HTMLFormElement,
    accessToken: string,
    onDone?: () => void
): () => void {
    let submitting = false;
    const onSubmit = async (e: Event) => {
        e.preventDefault();
        if (submitting) return;
        submitting = true;
        try {
            const fd = new FormData(formReset);
            const password = String(fd.get('password') ?? '');
            const password2 = String(fd.get('password2') ?? '');

            if (password.length < 6) {
                alert(t('auth.error_password_short'));
                return;
            }
            if (password !== password2) {
                alert(t('auth.error_password_mismatch'));
                return;
            }

            await apiRequest('/api/auth/reset-password', {
                method: 'POST',
                body: { access_token: accessToken, password },
            });

            alert(t('auth.reset_success'));
            onDone?.();
        } catch (err) {
            alert(t('auth.error_login') + (err instanceof Error ? err.message : String(err)));
        } finally {
            submitting = false;
        }
    };
    formReset.addEventListener('submit', onSubmit);
    return () => formReset.removeEventListener('submit', onSubmit);
}
